import { Helmet } from "react-helmet-async";

const SITE_URL = "https://mistmedia.com";
const DEFAULT_IMAGE = "/api/uploads/hero-fallback.jpg";

export function PageSEO({ title, description, path = "", image, type = "website", schema }) {
  const fullTitle = title ? `${title} | Mist Media` : "Mist Media | Film, Television & Digital Production Company";
  const fullDescription = description || "Mist Media is a future-forward production company creating bold stories across film, television, and digital.";
  const fullUrl = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Mist Media" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Schema.org JSON-LD */}
      {schema && !Array.isArray(schema) && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
      {schema && Array.isArray(schema) && schema.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

export function ProjectSchema(project) {
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": project.title,
    "description": project.synopsis,
    "genre": project.genre,
    "image": project.poster_image,
    "url": `${SITE_URL}/projects/${project.slug}`,
    "datePublished": project.release_date,
    "productionCompany": {
      "@type": "Organization",
      "name": "Mist Media",
      "url": SITE_URL
    },
    ...(project.cast_crew && {
      "contributor": project.cast_crew.split(", ").map(person => ({
        "@type": "Person",
        "name": person.replace(/^(Director|Stars|Star|Producer|Writer):\s*/i, "")
      }))
    }),
    ...(project.teaser_video && {
      "trailer": {
        "@type": "VideoObject",
        "name": `${project.title} - Teaser`,
        "embedUrl": project.teaser_video
      }
    })
  };
}

export function BreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  };
}

export function ProjectsCollectionSchema(projects) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Mist Media Projects",
    "description": "Explore the complete slate of Mist Media productions spanning drama, horror, thriller, and comedy across film, television, and digital.",
    "url": `${SITE_URL}/projects`,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": projects.map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `${SITE_URL}/projects/${p.slug}`,
        "name": p.title
      }))
    }
  };
}

export function AboutPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Mist Media",
    "description": "Learn about Mist Media, a future-forward production company dedicated to creating bold, compelling narratives across film, television, and digital platforms.",
    "url": `${SITE_URL}/about`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Mist Media",
      "description": "A future-forward production company creating bold stories across film, television, and digital.",
      "foundingDate": "2022",
      "areaServed": "Worldwide",
      "knowsAbout": ["Film Production", "Television Production", "Digital Content Creation"],
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Los Angeles",
        "addressRegion": "CA",
        "addressCountry": "US"
      }
    }
  };
}

export function ContactPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Mist Media",
    "description": "Get in touch with Mist Media for collaborations, inquiries, or to learn more about our film, television, and digital productions.",
    "url": `${SITE_URL}/contact`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Mist Media",
      "email": "hello@mistmedia.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Los Angeles",
        "addressRegion": "CA",
        "addressCountry": "US"
      }
    }
  };
}

export function FAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}
